/**
 * Composable per OData query building autosufficiente
 * Fornisce funzionalità OData senza dependency injection
 */

import buildQuery from 'odata-query';
import type { ODataContext } from '~/mixins/filterHandler/types';

/**
 * Composable autosufficiente per costruzione query OData V4
 *
 * @description
 * Fornisce funzionalità di query building OData senza richiedere dependency injection.
 * Può essere utilizzato in qualsiasi component/composable senza setup aggiuntivo.
 *
 * @returns Oggetto con funzioni per costruzione query OData
 *
 * @example
 * // Uso base
 * const { buildOdataQuery } = useODataBuilder();
 * const query = buildOdataQuery({
 *   count: true,
 *   filter: { name: { startswith: 'Mario' } },
 *   top: 20
 * });
 *
 * @example
 * // Uso nei composables autocomplete
 * const { buildOdataQuery, createContext } = useODataBuilder();
 * const context = createContext({ count: true });
 * context.filter = { customerId: { eq: 123 } };
 * const query = buildOdataQuery(context);
 */
export function useODataBuilder() {
	/**
	 * Costruisce una query OData V4 dal contesto fornito
	 *
	 * @param context - Contesto OData con filtri, paginazione, etc.
	 * @returns Query string OData V4
	 */
	const buildOdataQuery = (context: ODataContext): string => {
		return buildQuery(context);
	};

	/**
	 * Crea un contesto OData base
	 *
	 * @param initialContext - Contesto iniziale (opzionale)
	 * @returns Nuovo contesto OData
	 */
	const createContext = (initialContext: Partial<ODataContext> = {}): ODataContext => {
		return {
			count: true,
			...initialContext,
		} as ODataContext;
	};

	/**
	 * Aggiunge un filtro al contesto esistente
	 *
	 * @param context - Contesto OData da modificare
	 * @param filterKey - Chiave del filtro
	 * @param filterValue - Valore del filtro
	 */
	const addFilter = (context: ODataContext, filterKey: string, filterValue: any): void => {
		if (!context.filter) {
			context.filter = {};
		}
		context.filter[filterKey] = filterValue;
	};

	/**
	 * Aggiunge espansione al contesto
	 *
	 * @param context - Contesto OData da modificare
	 * @param expandPath - Percorso dell'espansione
	 * @param selectFields - Campi da selezionare (opzionale)
	 */
	const addExpand = (context: ODataContext, expandPath: string, selectFields?: string): void => {
		if (!context.expand) {
			context.expand = {};
		}

		const pathParts = expandPath.split('.');
		let currentExpand = context.expand;

		for (let i = 0; i < pathParts.length; i++) {
			const part = pathParts[i];
			if (i === pathParts.length - 1) {
				// Ultimo elemento
				if (!currentExpand[part]) {
					currentExpand[part] = {};
				}
				if (selectFields) {
					currentExpand[part].select = selectFields;
				}
			} else {
				// Elemento intermedio
				if (!currentExpand[part]) {
					currentExpand[part] = {};
				}
				if (!currentExpand[part].expand) {
					currentExpand[part].expand = {};
				}
				currentExpand = currentExpand[part].expand!;
			}
		}
	};

	return {
		buildOdataQuery,
		createContext,
		addFilter,
		addExpand,
	};
}

/**
 * Tipo di ritorno del composable useODataBuilder
 */
export type ODataBuilderComposable = ReturnType<typeof useODataBuilder>;

// Rimuoviamo completamente il sistema di dependency injection
// I composables ora sono autosufficenti
